import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { TabellaDinamica } from 'src/app/components/tabella-cfg-dinamica/tabella-cfg-dinamica.component';
import { DataService } from 'src/app/components/totali-cruscotto/data-service.interface';
import { Ambito } from '../ambiti/ambiti.service';
import { GruppoCfgStampa } from '../config-stampe/config-stampe.service';
import { InformativaAziendale } from '../informativa-aziendale/informativa-aziendale.service';
import { Nace, Paese } from '../json-file/json-file.service';
import { QuestionarioSostenibileVsme } from '../questionari/questionari.service';
import { Settore, SottoSettore } from '../settori/settori.service';
import { STDR } from '../standard-rendicontazione/standard-rendicontazione.service';
import { OpzioneTematica } from '../tematiche/tematiche.service';
import { UtilityService } from '../utility/utility.service';
import { ReportVsmeWebService } from './report-vsme-web.service';

export interface ReportVsme {
  id?: string;
  idAzienda?: string;
  anno?: string;
  fatturato?: number;
  nrDipendenti?: number;
  stdr?: STDR[];
  compliance?: STDR[];
  nome?: string;
  stato?: string;
  tabellaSiti?: TabellaDinamica;
  formaGiuridica?: string;
  paeseOperazioni?: Paese;
  codiciNace?: Nace;
  statoPatrimoniale?: number;
  tipo: 'BASE' | 'COMPLETO';
  settori?: Settore[];
  sottoSettori?: SottoSettore[];
  idMaterialita?: string;
  idDoppiaMaterialita?: string;
  idSintesiSostenibilita?: string;
  idAutovalutazioneCompilata?: string;
  idPolicySostenibilita?: string;
  idPianoSostenibilita?: string;
  idBilancioSostenibilita?: string;
  tipoAzienda?: 'MICROIMPRESA' | 'PMI' | undefined;
  idDatasourceAi?: string;
  folliaStatus?: string;
  folliaErrorMessage: string;
}

export interface MotivazioneTematica {
  idAmbito: string;
  idTematica: string;
  motivazione: string;
}

export interface CompilaTematiche {
  ambiti: Ambito[];
  stato: string;
  tipo: string;
  tipoAzienda: string;
  idAzienda: string;
  esercizio?: string[];
}

/** Durata in ms delle snack bar di errore brevi. */
const SNACK_DURATION_SHORT = 2000;

@Injectable({
  providedIn: 'root',
})
export class ReportVsmeService implements DataService {
  constructor(
    private readonly reportVsmeWebService: ReportVsmeWebService,
    private readonly utilityService: UtilityService,
  ) {}

  getTotaliCruscottoDS(idModulo: string): Observable<any> {
    return this.getTotaliCruscotto(idModulo);
  }

  public getReportVsme(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.reportVsmeWebService.get(page, pageSize, ricerca, filters, sortBy);
  }

  public delete(idModulo: any): Observable<any> {
    return this.reportVsmeWebService.delete(idModulo);
  }

  public archivia(idModulo: any) {
    return this.reportVsmeWebService.archivia(idModulo);
  }

  public disarchivia(idModulo: any, idSettore: string) {
    return this.reportVsmeWebService.disarchivia(idModulo, idSettore);
  }

  /* ---------------- C R E A Z I O N E  V S M E ---- step1 ------------*/

  /**
   * Creazione step 1 reportVsme
   * @param reportVsmeSostenibilita oggetto reportVsme di sostenibilita
   * @returns
   */
  public postCreazioneReportVsmeStep1(reportVsmeSostenibilita: any) {
    return this.reportVsmeWebService.postCreazioneReportVsmeStep1(reportVsmeSostenibilita);
  }

  /**
   * Aggiornamento step 1 reportVsme
   * @param reportVsmeSostenibilita id reportVsme di sostenibilita
   * @returns
   */
  public putAggiornamentoReportVsmeStep1(
    idReportVsmeSostenibilita: any,
    fatturato: number,
    nrDipendenti: number,
  ) {
    return this.reportVsmeWebService.putAggiornamentoReportVsmeStep1(
      idReportVsmeSostenibilita,
      fatturato,
      nrDipendenti,
    );
  }

  /* -------------- I N F O R M A T I V A   A Z I E N D A L E  step2 -----*/

  putSalvaCompilazioneInformativa(idReportVsmeSostenibilita: string, infoAz: InformativaAziendale) {
    return this.reportVsmeWebService.putSalvaCompilazioneInformativa(
      idReportVsmeSostenibilita,
      infoAz,
    );
  }
  putSalvaCompilazioneInformativaBozza(
    idReportVsmeSostenibilita: string,
    infoAz: InformativaAziendale,
  ) {
    return this.reportVsmeWebService.putSalvaCompilazioneInformativaBozza(
      idReportVsmeSostenibilita,
      infoAz,
    );
  }

  getCompilazioneInformativa(idReportVsmeSostenibilita: string) {
    return this.reportVsmeWebService.getCompilazioneInformativa(idReportVsmeSostenibilita);
  }

  getInformativa(idReportVsmeSostenibilita: string) {
    return this.reportVsmeWebService.getInformativa(idReportVsmeSostenibilita);
  }

  putCambiaFiguraInformativa(idReportVsmeSostenibilita: string, idFigura: string) {
    return this.reportVsmeWebService.putCambiaFiguraInformativa(
      idReportVsmeSostenibilita,
      idFigura,
    );
  }

  inviaMailInformativa(idReportVsmeSostenibilita: string) {
    return this.reportVsmeWebService.inviaMailInformativa(idReportVsmeSostenibilita);
  }

  inviaNotificaInformativa(idReportVsmeSostenibilita: string) {
    return this.reportVsmeWebService.inviaNotificaInformativa(idReportVsmeSostenibilita);
  }

  /* ------------------------- T E M A T I C H E step 3 -------------------------------- */

  public getQuestionarioReportVsme(idReportVsmeSostenibilita: string) {
    return this.reportVsmeWebService.getQuestionarioReportVsme(idReportVsmeSostenibilita);
  }

  /**
   *
   * @param
   * @returns
   */
  public putQuestionarioReportVsme(
    idReportVsmeSostenibilita: string,
    questionario: QuestionarioSostenibileVsme,
  ) {
    return this.reportVsmeWebService.putQuestionarioReportVsme(
      idReportVsmeSostenibilita,
      questionario,
    );
  }

  /**
   * Salvataggio delle motivazioni per la quale la tematica non è stata tratta
   * @param idReportVsmeSostenibilita
   * @param motivazioneTematica
   * @returns
   */
  public salvaMotivazioneTematica(
    idReportVsmeSostenibilita: string,
    motivazioneTematica: MotivazioneTematica[],
  ) {
    return this.reportVsmeWebService.putSalvaMotivazioneTematica(
      idReportVsmeSostenibilita,
      motivazioneTematica,
    );
  }

  /**
   * ASSEGNA FIGURA AGLI ELEMENTI DEL REPORT
   * @param idVsme
   * @param chiaveRisposta
   * @returns <Status 200 (senza body)>
   */

  public putAssegnaFiguraElementoVsme(
    idVsme: string,
    idAmbito: string,
    idTematica: string,
    chiaveRisposta: string,
    idSottoRisposta: string,
    idFigura: string,
  ) {
    return this.reportVsmeWebService.putAssegnaFiguraElementoVsme(
      idVsme,
      idAmbito,
      idTematica,
      chiaveRisposta,
      idSottoRisposta,
      idFigura,
    );
  }

  public salvaCompilazione(idReportVsme: string, ambiti: Ambito[]) {
    return this.reportVsmeWebService.salvaCompilazione(idReportVsme, ambiti);
  }

  /* ------------------------- C R U S C O T T O     STEP 9  -------------------------------- */
  public getTotaliCruscotto(idReportVsme: string) {
    return this.reportVsmeWebService.getTotaliCruscotto(idReportVsme);
  }

  /**
   * Funzione per selezionare le righe da far vedere nella grid
   * @param page pagina
   * @param pageSize record per pagina
   * @param ricerca risultato per ricerca
   * @param filters valori da filtrare
   * @param sortBy sort asc desc
   * @returns lista di righe
   */

  public getCruscotti(
    idReportVsme: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.reportVsmeWebService.getCruscotti(
      idReportVsme,
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  }

  /**
   * Cambio figura
   * @param idReportVsme
   * @param idFiguraPrecedente
   * @param idFiguraNuova
   * @returns
   */
  putCambiaFigura(idReportVsme: string, idFiguraPrecedente: string, idFiguraNuova: string) {
    return this.reportVsmeWebService.putCambiaFigura(
      idReportVsme,
      idFiguraPrecedente,
      idFiguraNuova,
    );
  }

  public inviaMailMultiple(idReportVsme: string) {
    return this.reportVsmeWebService.inviaMailMultiple(idReportVsme);
  }

  public inviaMail(idReportVsme: string, idFigura: string) {
    return this.reportVsmeWebService.inviaMail(idReportVsme, idFigura);
  }

  public inviaNotificaMultiple(idReportVsme: string) {
    return this.reportVsmeWebService.inviaNotificaMultiple(idReportVsme);
  }

  public inviaNotifica(idReportVsme: string, idFigura: string) {
    return this.reportVsmeWebService.inviaNotifica(idReportVsme, idFigura);
  }
  public putTemplateMail(idReportVsme: string, templateMail: any) {
    return this.reportVsmeWebService.putTemplateMail(idReportVsme, templateMail);
  }

  public getTemplateMail(idReportVsme: string) {
    return this.reportVsmeWebService.getTemplateMail(idReportVsme);
  }

  public pubblicaReportVsme(idReportVsme: string) {
    return this.reportVsmeWebService.pubblicaReportVsme(idReportVsme);
  }

  /* COMPILAZINE FIGURE */

  public getElementoDaCompilare(
    idReportVsme: string,
    idAmbito: string,
    idTematica: string,
    idElemento: string,
    chiaveRisposta: string,
  ) {
    return this.reportVsmeWebService.getElementoDaCompilare(
      idReportVsme,
      idAmbito,
      idTematica,
      idElemento,
      chiaveRisposta,
    );
  }

  public postElementoDaCompilare(
    idReportVsme: string,
    idAmbito: string,
    idTematica: string,
    idElemento: string,
    risposta: string | number,
    idCategoriaUnitaDiMisura?: string,
    idUnitaDiRiferimento?: string,
    chiaveRisposta?: string,
    dynamicTable?: TabellaDinamica,
    infoNonDisponibile?: {
      checked: false;
      risposta: string;
    },
    infoNonRilevante?: {
      checked: false;
      risposta: string;
    },
  ) {
    return this.reportVsmeWebService.postElementoDaCompilare(
      idReportVsme,
      idAmbito,
      idTematica,
      idElemento,
      risposta,
      idCategoriaUnitaDiMisura,
      idUnitaDiRiferimento,
      chiaveRisposta,
      dynamicTable,
      infoNonDisponibile,
      infoNonRilevante,
    );
  }

  public getElementiEsterniDaCompilare(temporaryToken: string) {
    return this.reportVsmeWebService.getElementiEsterniDaCompilare(temporaryToken);
  }

  public postElementiEsterniDaCompilare(temporaryToken: string, objEsterna: any[]) {
    return this.reportVsmeWebService.postElementiEsterniDaCompilare(temporaryToken, objEsterna);
  }

  public invioTestMail(messaggio: string, email: string, idReportVsme: string) {
    return this.reportVsmeWebService.invioTestMail(messaggio, email, idReportVsme);
  }

  /* COMPILAZIONE ESTERNA */
  public getAnteprimaReportVsmeCompilato(idReportVsme: string, idAzienda: string) {
    return this.reportVsmeWebService.getAnteprimaReportVsmeCompilato(idReportVsme, idAzienda);
  }

  /* ------------------------- S T A M P A   STEP 10  -------------------------------- */

  /**
   * GET Configurazioni stampa SINTESI (set intero, il filtro lo facciamo in frontend - raggruppare per tematica)
   * @param idReportVsme
   * @returns List<GruppoConfigRisultatiSintesiValori>
   */
  public getConfigStampa(idReportVsme: string) {
    return this.reportVsmeWebService.getConfigStampa(idReportVsme);
  }
  public postSalvaElementiStampa(idReportVsme: string, cfgStampaBilancio: GruppoCfgStampa) {
    return this.reportVsmeWebService.postSalvaElementiStampa(idReportVsme, cfgStampaBilancio);
  }
  public postStampaReportVsme(idReportVsme: string) {
    return this.reportVsmeWebService.postStampaReportVsme(idReportVsme);
  }

  public postStampaInforamtiva(idReportVsme: string) {
    return this.reportVsmeWebService.postStampaInforamtiva(idReportVsme);
  }

  /* prompt */
  public getGenericPrompt(idReportVsme: string) {
    return this.reportVsmeWebService.getGenericPrompt(idReportVsme);
  }

  // ─── Controllo motivazione e risposte obbligatorie VSME ──────────────────────────────────────────────────────

  /**
   * Controllo ricorsivo che mi controlla che tutte le risposte di tipo modulo base siano compilate, se non lo sono setto touched sui campi e ritorno false
   * @param risposteVsme
   * @returns
   */
  public ctrRisposteVsmeCompilate(risposteVsme: OpzioneTematica[]): boolean {
    return risposteVsme.every((rispostaVsme) => {
      // Se non ci sono sottoOpzioni, consideriamo il ramo come "valido"
      if (!rispostaVsme.sottoOpzioni) return true;

      const isCompilato = rispostaVsme.sottoOpzioni.every((sottoOpz) => {
        // Se è modulo completo, salto il controllo perchè non è obbligatorio compilare
        if (sottoOpz.moduloCompleto) return true;

        return this.isElementoCompilato(sottoOpz);
      });
      return isCompilato;
    });
  }

  /**
   * Controllo che una sottoopzne abbia una risposta valida
   * @param sottoOpzione
   * @returns
   */
  private isElementoCompilato(sottoOpzione: OpzioneTematica): boolean {
    const { notRisposta, infoNonDisponibile: nonDisp, infoNonRilevante: nonRilev } = sottoOpzione;

    // CASO 1: Risposta standard (se notRisposta false allora non ho bisogno di giustificare)controllo
    // direttamente la risposta originale
    if (!notRisposta) {
      let isCompilato = false;

      switch (sottoOpzione.tipo) {
        case 'checkbox':
        case 'azione':
          isCompilato = this.isAzioneOrCheckboxCompilato(sottoOpzione);
          break;

        case 'kpi':
          isCompilato = this.isKpiCompilato(sottoOpzione);
          break;

        case 'dynamic_table':
          isCompilato = this.isTabellaCompilata(sottoOpzione);
          break;

        default:
          return isCompilato;
      }

      if (isCompilato) {
        this.resetCampiAlternativi(sottoOpzione);
      }

      return isCompilato;
    }

    // CASO 2: Controllo le giustificazioni (non disponibile oppure non rilevante)
    if (!nonDisp || !nonRilev) return false;

    const ndValido = nonDisp.checked && !!nonDisp.risposta;
    const nrValido = nonRilev.checked && !!nonRilev.risposta;

    // Determiniamo la validità in base a cosa è selezionato
    let valido = false;
    // 1. Se checkato "non disopnibile", controllo se la risposta valida
    if (nonDisp.checked) {
      valido = !!ndValido;
      // 2. Se checkato "non rilevante", controllo se la risposta valida
    } else if (nonRilev.checked) {
      valido = !!nrValido;
    }

    // Se valido e sono in modalità "notRisposta", pulisco la risposta principale

    if (valido) {
      switch (sottoOpzione.tipo) {
        case 'checkbox':
        case 'azione':
          sottoOpzione.risposta = '';
          break;

        case 'kpi':
          sottoOpzione.valoriKpi[0].valoreAttribuito = undefined;
          break;
        case 'dynamic_table':
          this.pulisciTabella(sottoOpzione);
          break;

        default:
      }
    }
    if (!valido) {
      this._showSnack(
        `Spiega perché il dato non è disponibile oppure non rilevante`,
        SNACK_DURATION_SHORT,
      );
    }

    return valido;
  }

  // Metodo di supporto per isolare la logica di pulizia
  private resetCampiAlternativi(opzione: OpzioneTematica): void {
    if (opzione.infoNonDisponibile) {
      opzione.infoNonDisponibile.checked = false;
      opzione.infoNonDisponibile.risposta = '';
    }
    if (opzione.infoNonRilevante) {
      opzione.infoNonRilevante.checked = false;
      opzione.infoNonRilevante.risposta = '';
    }
  }

  /**
   * Mi controlla che la risposta di una checkbox oppure di un azione sia valida
   * @param sottoOpzione
   * @returns
   */
  private isAzioneOrCheckboxCompilato(sottoOpzione: OpzioneTematica): boolean {
    const isValido = !!sottoOpzione.risposta;

    if (!isValido) {
      this._showSnack(`La compilazione della risposta è obbligatoria`, SNACK_DURATION_SHORT);
    }

    return isValido;
  }

  /**
   * Mi controlla che la risposta del KPI sia valida
   * @param sottoOpzione
   * @returns
   */
  private isKpiCompilato(sottoOpzione: OpzioneTematica): boolean {
    const isKpiCompilato = !!(
      sottoOpzione.valoriKpi[0].idCategoriaUnitaDiMisura &&
      sottoOpzione.valoriKpi[0].idUnitaDiRiferimento &&
      sottoOpzione.valoriKpi?.[0]?.valoreAttribuito
    );

    if (!isKpiCompilato) {
      this._showSnack(`La compilazione del KPI è obbligatoria`, SNACK_DURATION_SHORT);
    }

    return isKpiCompilato;
  }

  /**
   * Controllo che una sotto-opzione di tipo tabella sia compilata, ovvero che abbia tutte le celle compilate
   * @param sottoOpzione
   * @returns
   */
  private isTabellaCompilata(sottoOpzione: OpzioneTematica): boolean {
    if (!sottoOpzione.righe || sottoOpzione.righe.length === 0) {
      this._showSnack('La compilazione della tabella è obbligatoria', SNACK_DURATION_SHORT);
      return false;
    }
    const esito = sottoOpzione.righe.every((riga) => {
      return Object.values(riga.celle).every((contenutoCella) => {
        return Object.values(contenutoCella).every(
          (valore) => valore !== null && valore !== undefined && valore !== '',
        );
      });
    });

    if (!esito) {
      this._showSnack('Compilare tutte le celle della tabella', SNACK_DURATION_SHORT);
    }
    return esito;
  }

  /**
   * Metodo per pulire tutte le celle della tabella
   * @param sottoOpzione
   */
  private pulisciTabella(sottoOpzione: OpzioneTematica): void {
    // Verifico che esistano righe da pulire
    sottoOpzione.righe?.forEach((riga) => {
      // Itero attraverso ogni cella della riga
      Object.values(riga.celle).forEach((contenutoCella) => {
        // Itero attraverso ogni chiave del contenuto della cella
        Object.keys(contenutoCella).forEach((key) => {
          // Pulisco il campo
          contenutoCella[key] = null;
        });
      });
    });
  }

  /**
   * Mostra una snack bar di errore con messaggio e durata configurabili.
   *
   * @param messaggio Testo da visualizzare nella snack bar.
   * @param durata    Durata in millisecondi.
   */
  private _showSnack(messaggio: string, durata: number): void {
    this.utilityService.openSnackBar(messaggio, '', {
      duration: durata,
      panelClass: ['red-snackbar'],
    });
  }
}
